import { useEffect, useRef, useState } from 'react';
import { RefreshCw, Send, CheckCircle2, XCircle, Clock, AlertCircle, Plus, Power, RotateCw, LogOut, QrCode } from 'lucide-react';
import { api } from '../api/client';
import { Badge } from '../components/ui/Badge';

export function AdminWhatsappPage() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [sessoes, setSessoes] = useState([]);
  const [loadingSessoes, setLoadingSessoes] = useState(false);
  const [outbox, setOutbox] = useState({ mensagens: [], stats: [] });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [novaSessao, setNovaSessao] = useState('');
  const [criandoSessao, setCriandoSessao] = useState(false);
  const [sessaoDetail, setSessaoDetail] = useState(null); // { name, status, me } da sessão configurada
  const [qr, setQr] = useState(null); // {mimetype, data}
  const [qrErro, setQrErro] = useState(null);
  const [acaoBusy, setAcaoBusy] = useState(null); // 'start'|'stop'|...
  const qrPollRef = useRef(null);

  const loadConfig = async () => {
    const r = await api.get('/whatsapp-config');
    setConfig(r.config);
    setForm({
      provider: r.config.provider,
      waha_base_url: r.config.waha_base_url || '',
      waha_session: r.config.waha_session || '',
      waha_api_key: '',
      queue_interval_ms: r.config.queue_interval_ms || 30000,
    });
  };
  const loadOutbox = async () => {
    const r = await api.get('/whatsapp-config/outbox?limit=30');
    setOutbox(r);
  };

  useEffect(() => { loadConfig(); loadOutbox(); }, []);
  useEffect(() => {
    const t = setInterval(loadOutbox, 10000);
    return () => clearInterval(t);
  }, []);

  // Poll do status da sessão configurada (quando provider=waha + session salva)
  useEffect(() => {
    if (!config || config.provider !== 'waha' || !config.waha_session) {
      setSessaoDetail(null);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/whatsapp-config/sessions/${encodeURIComponent(config.waha_session)}`);
        if (!cancelled) setSessaoDetail(r.sessao);
      } catch (e) {
        if (!cancelled) setSessaoDetail({ name: config.waha_session, status: 'ERRO', error: e.message });
      }
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [config?.provider, config?.waha_session]);

  // Quando status == SCAN_QR_CODE, baixa o QR e refaz a cada 20s (QR expira)
  useEffect(() => {
    if (sessaoDetail?.status === 'SCAN_QR_CODE') {
      loadQR();
      qrPollRef.current = setInterval(loadQR, 20000);
      return () => { if (qrPollRef.current) clearInterval(qrPollRef.current); };
    }
    setQr(null);
    setQrErro(null);
  }, [sessaoDetail?.status, sessaoDetail?.name]);

  const loadQR = async () => {
    if (!sessaoDetail?.name) return;
    try {
      const r = await api.get(`/whatsapp-config/sessions/${encodeURIComponent(sessaoDetail.name)}/qr`);
      setQr({ mimetype: r.mimetype || 'image/png', data: r.data });
      setQrErro(null);
    } catch (e) {
      setQrErro(e.message);
      setQr(null);
    }
  };

  const carregarSessoes = async () => {
    setLoadingSessoes(true);
    setMsg(null);
    try {
      const r = await api.get('/whatsapp-config/sessions');
      setSessoes(r.sessoes || []);
    } catch (e) {
      setMsg({ tipo: 'erro', texto: `Falha ao listar sessões: ${e.message}` });
    } finally { setLoadingSessoes(false); }
  };

  const salvar = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const payload = {
        provider: form.provider,
        waha_base_url: form.waha_base_url || null,
        waha_session: form.waha_session || null,
        queue_interval_ms: parseInt(form.queue_interval_ms, 10) || 30000,
      };
      if (form.waha_api_key?.trim()) payload.waha_api_key = form.waha_api_key.trim();
      const r = await api.patch('/whatsapp-config', payload);
      setConfig(r.config);
      setForm((f) => ({ ...f, waha_api_key: '' }));
      setMsg({ tipo: 'ok', texto: 'Configuração salva.' });
    } catch (e) {
      setMsg({ tipo: 'erro', texto: e.message });
    } finally { setSaving(false); }
  };

  const testar = async () => {
    setTesting(true);
    setMsg(null);
    try {
      const r = await api.post('/whatsapp-config/test', {});
      setMsg({ tipo: 'ok', texto: `Teste enviado via ${r.via}. Veja seu WhatsApp.` });
      loadOutbox();
    } catch (e) {
      setMsg({ tipo: 'erro', texto: `Teste falhou: ${e.message}` });
    } finally { setTesting(false); }
  };

  const criarSessao = async () => {
    if (!novaSessao.trim()) return;
    setCriandoSessao(true);
    setMsg(null);
    try {
      const r = await api.post('/whatsapp-config/sessions', { nome: novaSessao.trim() });
      setMsg({ tipo: 'ok', texto: `Sessão "${r.sessao.name}" criada. Escolha-a e escaneie o QR.` });
      setForm((f) => ({ ...f, waha_session: r.sessao.name }));
      setNovaSessao('');
      await carregarSessoes();
    } catch (e) {
      setMsg({ tipo: 'erro', texto: e.message });
    } finally { setCriandoSessao(false); }
  };

  const acaoSessao = async (acao) => {
    if (!sessaoDetail?.name) return;
    setAcaoBusy(acao);
    setMsg(null);
    try {
      await api.post(`/whatsapp-config/sessions/${encodeURIComponent(sessaoDetail.name)}/${acao}`, {});
      setMsg({ tipo: 'ok', texto: `Ação "${acao}" disparada.` });
    } catch (e) {
      setMsg({ tipo: 'erro', texto: e.message });
    } finally { setAcaoBusy(null); }
  };

  if (!form) return <div className="loader-full"><span className="spinner" /></div>;

  const isWaha = form.provider === 'waha';
  const statByName = Object.fromEntries((outbox.stats || []).map((s) => [s.status, s.total]));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">WhatsApp</h1>
          <p className="page-subtitle">Provedor de envio de notificações e fila de mensagens.</p>
        </div>
        <button className="btn btn-secondary" onClick={testar} disabled={testing}>
          <Send size={16} /> {testing ? 'Enviando...' : 'Enviar teste pro meu WhatsApp'}
        </button>
      </div>

      {msg && (
        <div className="card" style={{ marginBottom: 14, borderColor: msg.tipo === 'ok' ? 'var(--c-success)' : 'var(--c-danger)' }}>
          <div className="card-body" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {msg.tipo === 'ok' ? <CheckCircle2 size={16} color="#16a34a" /> : <AlertCircle size={16} color="#dc2626" />}
            <span className="text-sm">{msg.texto}</span>
          </div>
        </div>
      )}

      <form className="card" onSubmit={salvar} style={{ marginBottom: 14 }}>
        <div className="card-body stack" style={{ gap: 14 }}>
          <div className="field">
            <label>Provedor</label>
            <div className="row-wrap" style={{ gap: 12 }}>
              <label className="row" style={{ gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="provider"
                  value="cloud_api"
                  checked={form.provider === 'cloud_api'}
                  onChange={() => setForm((f) => ({ ...f, provider: 'cloud_api' }))}
                />
                WhatsApp Cloud API (Meta) — número 0800 oficial
              </label>
              <label className="row" style={{ gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="provider"
                  value="waha"
                  checked={isWaha}
                  onChange={() => setForm((f) => ({ ...f, provider: 'waha' }))}
                />
                WAHA (self-hosted) — controle total da sessão
              </label>
            </div>
            <p className="text-xs muted" style={{ marginTop: 6 }}>
              Cloud API usa templates aprovados e respeita janela de 24h. WAHA envia texto direto e
              faz fila para não bloquear a sessão.
            </p>
          </div>

          {isWaha && (
            <>
              <div className={config.can_edit_api_key ? 'grid-2' : ''}>
                <div className="field">
                  <label>URL base da WAHA</label>
                  <input
                    className="input"
                    placeholder="https://wh.viptecnologia.com.br/api"
                    value={form.waha_base_url}
                    onChange={(e) => setForm((f) => ({ ...f, waha_base_url: e.target.value }))}
                  />
                </div>
                {config.can_edit_api_key && (
                  <div className="field">
                    <label>API Key</label>
                    <input
                      className="input"
                      type="password"
                      placeholder={config.waha_api_key_set ? `Salvo: ${config.waha_api_key_preview} (deixe vazio para manter)` : 'X-Api-Key'}
                      value={form.waha_api_key}
                      onChange={(e) => setForm((f) => ({ ...f, waha_api_key: e.target.value }))}
                    />
                  </div>
                )}
              </div>
              {!config.can_edit_api_key && config.waha_api_key_set && (
                <p className="text-xs muted">
                  Conexão com a WAHA já está autenticada. Caso precise trocar a chave, peça ao administrador técnico.
                </p>
              )}

              <div className="field">
                <label className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span>Sessão</span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={carregarSessoes} disabled={loadingSessoes}>
                    <RefreshCw size={12} /> {loadingSessoes ? 'Carregando...' : 'Carregar sessões'}
                  </button>
                </label>
                {sessoes.length > 0 ? (
                  <select
                    className="select"
                    value={form.waha_session}
                    onChange={(e) => setForm((f) => ({ ...f, waha_session: e.target.value }))}
                  >
                    <option value="">— escolha —</option>
                    {sessoes.map((s) => (
                      <option key={s.name} value={s.name}>
                        {s.name} {s.status === 'WORKING' ? '✅' : '⚠️'} {s.me?.pushName ? `· ${s.me.pushName}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="input"
                    placeholder="ex.: comunica_pmi"
                    value={form.waha_session}
                    onChange={(e) => setForm((f) => ({ ...f, waha_session: e.target.value }))}
                  />
                )}
                <div className="row-wrap" style={{ gap: 8, marginTop: 8 }}>
                  <input
                    className="input"
                    placeholder="nome para nova sessão (ex.: comunica_pmi)"
                    value={novaSessao}
                    onChange={(e) => setNovaSessao(e.target.value)}
                    style={{ flex: 1, minWidth: 200 }}
                  />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={criarSessao} disabled={criandoSessao || !novaSessao.trim()}>
                    <Plus size={12} /> {criandoSessao ? 'Criando...' : 'Criar nova sessão'}
                  </button>
                </div>
                <p className="text-xs muted" style={{ marginTop: 4 }}>
                  Criar nova já inicia a sessão — depois selecione-a aqui em cima, salve e escaneie o QR
                  no painel abaixo.
                </p>
              </div>

              <div className="field">
                <label>Intervalo entre envios da fila</label>
                <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                  <input
                    className="input"
                    type="number"
                    min={5000}
                    max={600000}
                    step={1000}
                    value={form.queue_interval_ms}
                    onChange={(e) => setForm((f) => ({ ...f, queue_interval_ms: e.target.value }))}
                    style={{ width: 140 }}
                  />
                  <span className="text-sm muted">ms ({(form.queue_interval_ms / 1000).toFixed(0)}s entre uma mensagem e outra)</span>
                </div>
              </div>
            </>
          )}

          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar configuração'}
            </button>
          </div>
        </div>
      </form>

      {/* Painel da sessão configurada */}
      {isWaha && config?.waha_session && sessaoDetail && (
        <SessaoPainel
          sessao={sessaoDetail}
          qr={qr}
          qrErro={qrErro}
          acaoBusy={acaoBusy}
          onAcao={acaoSessao}
          onReloadQR={loadQR}
        />
      )}

      <div className="card">
        <div className="card-body">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>Fila de envio (outbox)</h3>
            <div className="row-wrap" style={{ gap: 8 }}>
              <Badge color="warning">Pendentes: {statByName.pending || 0}</Badge>
              <Badge color="success">Enviadas: {statByName.sent || 0}</Badge>
              {statByName.failed > 0 && <Badge color="danger">Falhas: {statByName.failed}</Badge>}
              {statByName.sending > 0 && <Badge color="info">Enviando: {statByName.sending}</Badge>}
            </div>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Status</th><th>Destinatário</th><th>Mensagem</th><th>Tentativas</th><th>Criada</th><th>Enviada</th></tr>
              </thead>
              <tbody>
                {outbox.mensagens.length === 0 && (
                  <tr><td colSpan={6} className="muted text-sm" style={{ padding: 14, textAlign: 'center' }}>Nenhuma mensagem na fila ainda.</td></tr>
                )}
                {outbox.mensagens.map((m) => (
                  <tr key={m.id}>
                    <td>{statusBadge(m.status)}</td>
                    <td className="text-sm">{m.subscriber_name || m.phone_number}</td>
                    <td className="text-sm" style={{ maxWidth: 320 }}>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={m.body}>{m.body}</div>
                      {m.error_message && <div className="text-xs" style={{ color: '#dc2626' }}>{m.error_message}</div>}
                    </td>
                    <td className="text-sm">{m.retry_count}</td>
                    <td className="text-xs muted">{fmtDate(m.created_at)}</td>
                    <td className="text-xs muted">{m.sent_at ? fmtDate(m.sent_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function SessaoPainel({ sessao, qr, qrErro, acaoBusy, onAcao, onReloadQR }) {
  const status = sessao.status;
  const cor = status === 'WORKING' ? 'success'
    : status === 'SCAN_QR_CODE' ? 'warning'
    : status === 'STARTING' ? 'info'
    : 'danger';

  const explicacao = {
    WORKING: 'Sessão conectada — mensagens estão sendo enviadas.',
    SCAN_QR_CODE: 'Sessão aguardando escaneamento. Abra o WhatsApp no seu celular → Aparelhos conectados → Conectar um aparelho → escaneie o QR abaixo.',
    STARTING: 'Sessão iniciando...',
    STOPPED: 'Sessão parada. Clique em Iniciar.',
    FAILED: 'Sessão falhou (provavelmente foi deslogada do WhatsApp). Clique em Logout, depois Iniciar para gerar um novo QR.',
    ERRO: 'Não consegui consultar a sessão na WAHA.',
  }[status] || `Status: ${status}`;

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-body stack" style={{ gap: 12 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Sessão: <code>{sessao.name}</code></h3>
            <Badge color={cor}>{status}</Badge>
            {sessao.me?.pushName && <span className="text-sm muted">· {sessao.me.pushName} ({sessao.me.id})</span>}
          </div>
          <div className="row-wrap" style={{ gap: 6 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => onAcao('start')} disabled={!!acaoBusy} title="Iniciar">
              <Power size={12} /> Iniciar
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => onAcao('restart')} disabled={!!acaoBusy} title="Reiniciar">
              <RotateCw size={12} /> Reiniciar
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => onAcao('stop')} disabled={!!acaoBusy} title="Parar">
              <XCircle size={12} /> Parar
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => onAcao('logout')} disabled={!!acaoBusy} title="Logout (desconecta do WhatsApp)">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>

        <p className="text-sm">{explicacao}</p>
        {sessao.error && <p className="text-xs" style={{ color: '#dc2626' }}>{sessao.error}</p>}

        {status === 'SCAN_QR_CODE' && (
          <div className="row" style={{ gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            {qr ? (
              <img
                src={`data:${qr.mimetype};base64,${qr.data}`}
                alt="QR Code"
                style={{ width: 280, height: 280, border: '1px solid var(--c-border)', borderRadius: 8, background: '#fff' }}
              />
            ) : qrErro ? (
              <div className="text-sm" style={{ color: '#dc2626' }}>{qrErro}</div>
            ) : (
              <div className="text-sm muted">Carregando QR...</div>
            )}
            <div className="stack" style={{ gap: 6, maxWidth: 320 }}>
              <strong>Como conectar:</strong>
              <ol className="text-sm" style={{ margin: 0, paddingLeft: 18 }}>
                <li>Abra o WhatsApp no celular</li>
                <li>Toque em <strong>Aparelhos conectados</strong></li>
                <li>Toque em <strong>Conectar um aparelho</strong></li>
                <li>Escaneie o QR ao lado</li>
              </ol>
              <button className="btn btn-ghost btn-sm" onClick={onReloadQR} style={{ alignSelf: 'flex-start' }}>
                <QrCode size={12} /> Recarregar QR
              </button>
              <p className="text-xs muted">O QR expira em segundos e é renovado automaticamente a cada 20s.</p>
            </div>
          </div>
        )}

        {status === 'FAILED' && (
          <div className="text-xs muted">
            Se a sessão ficou inativa por muito tempo (telefone desligado 14+ dias) ou foi desconectada
            de outro lugar, ela cai pra FAILED. Não reconecta sozinha — é preciso clicar em <strong>Logout</strong>
            e depois <strong>Iniciar</strong> para gerar um QR novo.
          </div>
        )}
      </div>
    </div>
  );
}

function statusBadge(s) {
  if (s === 'sent') return <Badge color="success"><CheckCircle2 size={11} /> sent</Badge>;
  if (s === 'failed') return <Badge color="danger"><XCircle size={11} /> failed</Badge>;
  if (s === 'sending') return <Badge color="info"><Clock size={11} /> sending</Badge>;
  return <Badge color="warning"><Clock size={11} /> pending</Badge>;
}

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
